import { useMemo } from 'react'

import { CreatorPermission } from '@api'
import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import { $isCreatorProcessCompleted } from '@/models/creators.ts'
import { CreatorFeatures } from '@/modules/accounts/types.ts'
import { useCreators } from '@/modules/creators/hooks/useCreators.ts'
import { isActiveCreator } from '@/modules/creators/utils.ts'
import { statisticsService } from '@/modules/statistics/onlyfans/service/statistics-service.ts'
import { handleError } from '@/utils/error'

export const useCreatorOverviewSelect = (
  dateRange: DateRange,
  defaultSelectedCreators: boolean,
) => {
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const {
    selection: { selectedCreators },
  } = useCreators()

  const statisticsCreators = useMemo(
    () =>
      selectedCreators
        .filter(
          (creator) =>
            creator.permissions.includes(CreatorPermission.SEE_STATS) &&
            creator.features.includes(CreatorFeatures.stats) &&
            isActiveCreator(creator),
        )
        .map((creator) => creator.id),
    [selectedCreators],
  )
  const {
    data: overviewData,
    isFetching,
    isIdle,
    refetch,
  } = useQuery(
    statisticsService.getStatisticsKey(
      statisticsCreators,
      dateRange.from!,
      dateRange.to!,
    ),
    async () => {
      if (!dateRange || statisticsCreators.length === 0) return

      return await statisticsService.getCreatorStats(
        statisticsCreators,
        dateRange.from || new Date(),
        dateRange.to || new Date(),
      )
    },
    {
      enabled: isCreatorProcessCompleted,
      onError: (error) =>
        handleError(error, 'Failed to get creator overview data'),
    },
  )

  return useMemo(
    () => ({
      overviewData,
      isFetching: isFetching || isIdle || !defaultSelectedCreators,
      refetch,
    }),
    [overviewData, isFetching, isIdle, defaultSelectedCreators, refetch],
  )
}
